/**
 * Theme Editor Lifecycle Handler
 * Re-initializes dynamic components on shopify:section:load/unload/select
 * and handles deep-linking on shopify:block:select/deselect.
 */

(function () {
  'use strict';

  // Only run in browser environment
  if (typeof document === 'undefined') return;

  function reinitSection(section) {
    if (!section) return;

    // 1. Re-init Countdown Timers
    const countdowns = section.querySelectorAll('countdown-timer');
    countdowns.forEach((el) => {
      if (typeof el.update === 'function') {
        el.update();
      }
      if (typeof el.initCountdown === 'function') {
        el.initCountdown();
      }
    });

    // 2. Re-init Marquee Scrollers (supports both marquee-scroll and marquee-scroller)
    const marquees = section.querySelectorAll('marquee-scroll, marquee-scroller, [data-marquee]');
    marquees.forEach((el) => {
      if (typeof el.initObserver === 'function') {
        el.initObserver();
      }
      if (typeof el.initMarquee === 'function') {
        el.initMarquee();
      }
    });

    // 3. Re-init Hero Slideshows
    const slideshows = section.querySelectorAll('.hero-slideshow, [data-hero-slideshow], .slideshow');
    slideshows.forEach((slider) => {
      const slides = slider.querySelectorAll('.hero-slide, .slideshow__slide, [data-slide]');
      if (slides.length > 0) {
        slides.forEach((s, idx) => {
          s.classList.toggle('is-active', idx === 0);
          s.style.opacity = idx === 0 ? '1' : '0';
          s.style.visibility = idx === 0 ? 'visible' : 'hidden';
        });
      }
      const dots = slider.querySelectorAll('.hero-slideshow__dot, .hero-dot');
      if (dots.length > 0) {
        dots.forEach((d, idx) => d.classList.toggle('is-active', idx === 0));
      }
    });

    // 4. Re-init Accordions / Details
    const accordions = section.querySelectorAll('details.faq-item, details.accordion-item, .product-v2__accordion-item, .collapsible-item, details');
    accordions.forEach((item) => {
      const summary = item.querySelector('summary');
      if (summary && !summary._themeEditorBound) {
        summary._themeEditorBound = true;
        summary.addEventListener('click', () => {
          const isOpen = item.hasAttribute('open');
          summary.setAttribute('aria-expanded', !isOpen);
        });
      }
    });

    // 5. Re-init Sticky ATC
    const stickyAtc = section.querySelector('sticky-atc');
    if (stickyAtc && typeof stickyAtc.init === 'function') {
      stickyAtc.init();
    } else if (stickyAtc && typeof stickyAtc.initObserver === 'function') {
      stickyAtc.initObserver();
    }

    // 5b. Re-init Product Forms
    const productForms = section.querySelectorAll('product-form');
    productForms.forEach((form) => {
      if (typeof form.init === 'function') {
        form.init();
      }
    });

    // 6. Re-init ScrollReveal / animations
    if (typeof ScrollReveal !== 'undefined') {
      try { new ScrollReveal(); } catch (e) {}
    }
    if (typeof window.themeInitAnimations === 'function') {
      try { window.themeInitAnimations(section); } catch (e) {}
    }
  }

  // Handle section load event
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target;
    reinitSection(section);
  });

  // Handle section select event
  document.addEventListener('shopify:section:select', function (event) {
    const section = event.target;
    if (!section) return;
    reinitSection(section);
  });

  // Handle section unload event
  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target;
    if (!section) return;

    // Clean up countdown intervals if any
    const countdowns = section.querySelectorAll('countdown-timer');
    countdowns.forEach((el) => {
      if (el.interval) {
        clearInterval(el.interval);
      }
      if (el._timerInterval) {
        clearInterval(el._timerInterval);
      }
    });
  });

  // Handle block select event
  document.addEventListener('shopify:block:select', function (event) {
    const blockId = event.detail.blockId;
    const sectionId = event.detail.sectionId;
    if (!blockId) return;

    // 1. Locate block element by data-block-id, id, or Shopify editor attribute
    const blockEl =
      document.querySelector(`[data-block-id="${blockId}"]`) ||
      document.getElementById(`block-${blockId}`) ||
      document.querySelector(`[data-shopify-editor-block*="${blockId}"]`);

    if (!blockEl) return;

    // Case A: Block is or contains an Accordion / Collapsible Item (details element)
    const details =
      (blockEl.tagName === 'DETAILS' ? blockEl : null) ||
      blockEl.closest('details') ||
      blockEl.querySelector('details');

    if (details) {
      details.setAttribute('open', '');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');
      details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    // Case B: Block is or contains a Slide in a Slideshow
    const slide =
      blockEl.closest('.hero-slide, .slideshow__slide, [data-slide]') ||
      blockEl.querySelector('.hero-slide, .slideshow__slide, [data-slide]');

    if (slide) {
      const slider = slide.closest('.hero-slideshow, [data-hero-slideshow], .slideshow');
      if (slider) {
        const allSlides = slider.querySelectorAll('.hero-slide, .slideshow__slide, [data-slide]');
        allSlides.forEach((s) => {
          s.classList.remove('is-active');
          s.style.opacity = '0';
          s.style.visibility = 'hidden';
        });
        slide.classList.add('is-active');
        slide.style.opacity = '1';
        slide.style.visibility = 'visible';

        // Update dot pagination if present
        const slideIndex = Array.from(allSlides).indexOf(slide);
        const dots = slider.querySelectorAll('.hero-slideshow__dot, .hero-dot');
        if (dots.length > slideIndex) {
          dots.forEach((d) => d.classList.remove('is-active'));
          dots[slideIndex].classList.add('is-active');
        }
      }
      return;
    }

    // Case C: Announcement Bar item
    const announcementItem =
      blockEl.closest('.announcement-bar__item') ||
      (blockEl.classList.contains('announcement-bar__item') ? blockEl : null) ||
      blockEl.querySelector('.announcement-bar__item');

    if (announcementItem) {
      const parentBar = announcementItem.closest('.announcement-bar');
      if (parentBar) {
        parentBar.querySelectorAll('.announcement-bar__item').forEach((item) => item.classList.remove('is-active'));
        announcementItem.classList.add('is-active');
      }
      return;
    }

    // Case D: Tab Block
    const tabPane =
      blockEl.closest('.tab-content, [role="tabpanel"]') ||
      blockEl.querySelector('.tab-content, [role="tabpanel"]');

    if (tabPane) {
      const tabId = tabPane.id;
      const tabBtn = document.querySelector(`[aria-controls="${tabId}"], [data-target="${tabId}"]`);
      if (tabBtn) tabBtn.click();
      return;
    }

    // Case E: Quantity Breaks Block
    const qtyBreaks =
      blockEl.closest('.qty-breaks') ||
      blockEl.querySelector('.qty-breaks');

    if (qtyBreaks) {
      if (typeof window.initQuantityTiers === 'function') {
        window.initQuantityTiers(qtyBreaks.parentElement || document);
      }
      const firstTier = qtyBreaks.querySelector('.qty-breaks__item');
      if (firstTier && !qtyBreaks.querySelector('.qty-breaks__item.is-selected')) {
        firstTier.classList.add('is-selected');
      }
    }

    // Case F: Product Media Thumbnail
    const mediaThumb =
      blockEl.closest('.product-gallery__thumb-btn, [data-media-id]') ||
      blockEl.querySelector('.product-gallery__thumb-btn, [data-media-id]');

    if (mediaThumb && typeof mediaThumb.click === 'function') {
      mediaThumb.click();
    }

    // Smooth scroll into view for any selected block
    blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  // Handle block deselect event
  document.addEventListener('shopify:block:deselect', function (event) {
    // Keep UI graceful on deselect
  });
})();
